import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ClientFirmGroup, clientRepresentatives, getLobbyistRegistry, LobbyistRegistryIndex } from '../../data/cache';
import { RegistryLobbyist } from '../../types/types';
import { color, font, radius, space, weight } from '../../theme';
import { Card } from '../Card';
import Avatar from '../Avatar';
import Button from '../Button';
import { EmptyState, ErrorState } from '../States';
import { Skeleton, SkeletonText } from '../Skeleton';
import { BriefcaseIcon } from './RegistryBits';
import { INDEPENDENT_LABEL, registryLobbyistPath } from './registry';

/* "Who represents X" (decision 3D): a client of registered lobbyists, opened from global
   search (/search?client=…), with its lobbyists grouped by firm. Each name opens the
   lobbyist's page. */

const countLine = (n: number) => (n === 1 ? 'שדלן/ית רשום/ה אחד/ת מייצג/ת אותו' : `${n} שדלנים רשומים מייצגים אותו`);

// What's temporary about a lobbyist, for the caption beside the name: the permit, and how
// this client is represented
const temporaryNote = (l: RegistryLobbyist, client: string): string => {
  const notes: string[] = [];
  if (l.permitType === 'זמני') notes.push('היתר זמני');
  for (let i = 0; i < l.clients.length; i++) {
    if (l.clients[i].name === client && l.clients[i].kind === 'זמני') notes.push('ייצוג זמני');
  }
  return notes.join(' · ');
};

const FirmGroup = ({ group, client }: { group: ClientFirmGroup; client: string }) => (
  <section style={{ background: color.surfaceMuted, borderRadius: radius.lg, padding: space.md, display: 'flex', flexDirection: 'column', gap: space.sm }}>
    <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: space.xs + 2, fontSize: font.small, fontWeight: weight.bold, color: color.text }}>
      <span style={{ color: color.textSecondary }}><BriefcaseIcon size={15} /></span>
      <span>{group.firm || 'שדלנים עצמאיים'} · {group.lobbyists.length}</span>
    </h2>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: space.xs + 2 }}>
      {group.lobbyists.map(l => {
        const note = temporaryNote(l, client);
        return (
          <Link
            key={l.registryId}
            to={registryLobbyistPath(l)}
            state={{ from: client }}
            style={{
              display: 'flex', alignItems: 'center', gap: space.xs + 2, minHeight: 36, boxSizing: 'border-box',
              padding: `2px ${space.md - 2}px 2px ${space.xs}px`, borderRadius: radius.pill, background: color.surface,
              color: color.text, textDecoration: 'none', fontSize: font.small,
            }}
          >
            <Avatar name={l.fullName} size={26} />
            {l.fullName}
            {note && <span style={{ fontSize: font.caption, color: color.textTertiary }}>{note}</span>}
          </Link>
        );
      })}
    </div>
  </section>
);

const ClientView = ({ name, leading }: { name: string; leading?: React.ReactNode }) => {
  const navigate = useNavigate();
  const [reg, setReg] = useState<LobbyistRegistryIndex | null | undefined>(undefined);
  const [error, setError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setError(false);
    getLobbyistRegistry()
      .then(r => { if (!cancelled) setReg(r); })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [reloadKey]);

  const groups = reg ? clientRepresentatives(reg, name) : [];
  let count = 0;
  for (let i = 0; i < groups.length; i++) count += groups[i].lobbyists.length;

  let body: React.ReactNode;
  if (error) {
    body = <Card padding={0}><ErrorState onRetry={() => setReloadKey(k => k + 1)} /></Card>;
  } else if (reg === undefined) {
    body = (
      <div data-skeleton="true">
        <Card>
          <div style={{ display: 'flex', gap: space.md, alignItems: 'center', marginBottom: space.md }}>
            <Skeleton width={40} height={40} rounded={20} />
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: space.sm }}><Skeleton width="70%" height={18} /><Skeleton width="45%" height={12} /></div>
          </div>
          <SkeletonText lines={3} />
        </Card>
      </div>
    );
  } else if (count === 0) {
    body = (
      <Card padding={0}>
        <EmptyState
          icon="search"
          title="הלקוח לא נמצא בפנקס השדלנים"
          body="ייתכן שהקישור שגוי, או שהשם השתנה בפנקס."
          action={<Button onClick={() => navigate(`/search?q=${encodeURIComponent(name)}`, { replace: true })}>חיפוש בשם הזה</Button>}
        />
      </Card>
    );
  } else {
    body = (
      <div style={{ display: 'flex', flexDirection: 'column', gap: space.md }}>
        <Card>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: space.md }}>
            <span style={{ width: 40, height: 40, flexShrink: 0, borderRadius: '50%', background: color.surfaceMuted, color: color.textSecondary, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <BriefcaseIcon size={20} />
            </span>
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: space.xxs }}>
              <h1 style={{ margin: 0, fontSize: font.heading, fontWeight: weight.bold, lineHeight: 1.35, color: color.text }}>{name}</h1>
              <div style={{ fontSize: font.small, color: color.textSecondary }}>{countLine(count)}</div>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: space.sm, marginTop: space.md }}>
            {groups.map(g => <FirmGroup key={g.firm || INDEPENDENT_LABEL} group={g} client={name} />)}
          </div>
        </Card>
        <p style={{ margin: 0, fontSize: font.caption, color: color.textTertiary, lineHeight: 1.5 }}>
          מקור: פנקס השדלנים של הכנסת. הלקוחות הם הצהרות השדלנים בפנקס, ומקובצים לפי חברת השדלנות.
        </p>
      </div>
    );
  }

  return (
    <div style={{ width: '100%' }}>
      {leading && (
        <div style={{ position: 'sticky', top: 0, zIndex: 50, background: color.page, display: 'flex', alignItems: 'center', gap: space.xs, padding: `${space.sm}px 0` }}>
          {leading}
          <span style={{ fontSize: font.label, fontWeight: weight.medium, color: color.textSecondary }}>לקוחות של שדלנים</span>
        </div>
      )}
      <div style={{ paddingTop: space.sm }}>{body}</div>
    </div>
  );
};

export default ClientView;
